/**
 * Talk-track + email template generators and Tune logic (SCRUM-209), ported
 * from Ryan's ccGenerateTalkTrack / ccGenerateEmail / ccApplyTune.
 *
 * Templates interpolate the product name + valueProposition (a live A0 field)
 * and leave [Bracketed Tokens] for the banker to fill at send time. Three
 * structural variants per channel, plus concise / warm / formal styles.
 */
import type { Product } from '@/features/product-scan/product-scan.schema';

export interface EmailDraft {
  subject: string;
  body: string;
}

export type TuneStyle = 'concise' | 'warm' | 'formal' | null;

/** The 5 Tune preset chips (Ryan's exact set). */
export const TUNE_PRESETS: readonly string[] = [
  'Make it shorter',
  'Make it warmer',
  'More formal',
  'Lead with the rate',
  'Concise — 3 sentences max',
];

function vpOf(product: Product): string {
  return product.valueProposition ?? '[product value proposition]';
}

/** Picks a variant by index from a NON-EMPTY array (cycles via modulo). */
function cycle<T>(variants: readonly T[], index: number): T {
  const v = variants[((index % variants.length) + variants.length) % variants.length];
  if (v === undefined) throw new Error('cycle() called on empty array');
  return v;
}

/** Generates a talk track for the product, optionally in a tuned style. */
export function generateTalkTrack(product: Product, variant: number, style: TuneStyle): string {
  const name = product.name;
  const vp = vpOf(product);

  if (style === 'concise') {
    return `Hi [First Name], [Banker Name] from [Bank Name] — quick call.\n\nLooking at your account: [Holder Age] years old, [Tenure Years]-year relationship with us, current balance around [Current Balance Range], holding [Products Held]. You came up as a strong fit for our ${name} — ${vp}. Got 5 minutes?`;
  }
  if (style === 'warm') {
    return `Hi [First Name], it's [Banker Name] over at [Bank Name] — hope you're having a great week.\n\nI was reviewing accounts this morning and yours stood out. You've been with us [Tenure Years] years now, [Branch Name] customer, and right now you're holding [Products Held]. Based on where you are at [Holder Age], our ${name} looks like a real fit.\n\nThe value is ${vp}. Mind if I walk you through [Key Benefit 1] and [Key Benefit 2]?`;
  }
  if (style === 'formal') {
    return `Good [morning/afternoon], [First Name] [Last Name]. This is [Banker Name] calling from [Bank Name], [Branch Name] branch.\n\nI am reaching out regarding the ${name}. Our records indicate you have been a customer for [Tenure Years] years, currently hold [Products Held], and maintain a relationship balance of [Current Balance Range]. Based on your profile — [Holder Age] years of age, [Account Type] account — you meet eligibility on [Eligibility Met Count] of [Eligibility Total] criteria.\n\nWould you have a brief moment to discuss the terms?`;
  }

  const variants = [
    `OPENING\nHi [First Name], this is [Banker Name] from [Bank Name] — your [Branch Name] branch.\n\nWHY YOU\nI was reviewing customer accounts this week and yours flagged for our ${name}. Quick context on your profile:\n• Age: [Holder Age]\n• Tenure: [Tenure Years] years ([Account Open Date])\n• Current balance: [Current Balance Range]\n• Products held: [Products Held]\n\nWHY THIS PRODUCT\n• ${vp}\n• Rate: [Current Rate]%\n\nASK\nCan I grab [Length] minutes to walk through it?`,
    `Hi [First Name], [Banker Name] from [Bank Name] — quick favor.\n\nYour name surfaced on this week's ${name} list. You've been with us since [Account Open Date], currently holding [Products Held] with a [Current Balance Range] balance.\n\nThe one-line pitch: ${vp}\n\nWhy now: [Trigger Reason]. 10 minutes [Time Slot 1]?`,
    `[First Name] — hey, [Banker Name] from [Bank Name] here.\n\nReason for the call: you're on this week's ${name} shortlist. Your profile matched [Eligibility Met Count] of [Eligibility Total] criteria.\n\nIn one breath: ${vp}.\n\nThree options:\n  1) 10 min call [Time Slot 1]\n  2) One-pager email\n  3) Skip — I'll requeue. Which works?`,
  ] as const;
  return cycle(variants, variant);
}

/** Generates an email draft for the product, optionally in a tuned style. */
export function generateEmail(product: Product, variant: number, style: TuneStyle): EmailDraft {
  const name = product.name;
  const vp = vpOf(product);

  if (style === 'concise') {
    return {
      subject: `[First Name], an offer that fits your [Account Type] account — ${name}`,
      body: `Hi [First Name],\n\nQuick note: your [Tenure Years]-year relationship + [Products Held] profile makes you a strong fit for our ${name}.\n\n${vp}\n\n10 minutes [Time Slot 1]?\n\n[Banker Name]\n[Bank Name] · [Branch Name]`,
    };
  }
  if (style === 'warm') {
    return {
      subject: `Thinking of you, [First Name] — a ${name} that fits`,
      body: `Hi [First Name],\n\nHope all is well at [City]! I was reviewing accounts this morning and yours came to mind — you've been with [Bank Name] since [Account Open Date] ([Tenure Years] years), currently holding [Products Held].\n\nOur ${name} looks like a real fit. The value: ${vp}\n\nReply with a time, or call me direct at [Phone].\n\nWarmly,\n[Banker Name]\n[Branch Name] · [Bank Name]`,
    };
  }
  if (style === 'formal') {
    return {
      subject: `[First Name] [Last Name]: Eligibility confirmed for ${name}`,
      body: `Dear [First Name] [Last Name],\n\nFollowing a review of your account profile, we confirm your eligibility for the ${name} offered by [Bank Name].\n\nProfile summary:\n• Account type: [Account Type]\n• Relationship tenure: [Tenure Years] years\n• Current products: [Products Held]\n• Branch of record: [Branch Name]\n\nValue proposition: ${vp}\n\nPlease respond at your convenience to schedule a brief consultation.\n\nSincerely,\n[Banker Name], [Title]\n[Bank Name] · [Branch Name]`,
    };
  }

  const variants: EmailDraft[] = [
    {
      subject: `[First Name], a ${name} that fits your [Account Type] account`,
      body: `Hi [First Name],\n\nAs a [Tenure Years]-year customer of [Bank Name] ([Branch Name] branch), I wanted to share an opportunity that lines up with your account: our ${name}.\n\nWhy I'm flagging it for you:\n• Age: [Holder Age]\n• Products held: [Products Held]\n• Relationship balance: [Current Balance Range]\n\nThe offer in plain English: ${vp}\n\nIf the timing is right, I can grab [Length] minutes [Time Slot 1] to walk through specifics.\n\nBest,\n[Banker Name]\n[Title] · [Bank Name] · [Branch Name]`,
    },
    {
      subject: `Worth a look, [First Name] — ${name}`,
      body: `Hi [First Name],\n\nQuick note: your account flagged a strong fit for our ${name} this week:\n\n• Eligibility match: [Eligibility Met Count]/[Eligibility Total] criteria\n• Triggered by: [Signal Type] signal\n\nThe headline benefit: ${vp}\n\nI've attached a one-pager. If you want to talk it through, call [Phone] or reply.\n\n[Banker Name]\n[Bank Name] · [Branch Name]`,
    },
    {
      subject: `[First Name] — clean match for ${name} ([Eligibility Met Count]/[Eligibility Total] criteria)`,
      body: `Hi [First Name],\n\nKeeping this short. We compared your account against the ${name} eligibility:\n\nYour profile\n• Account type: [Account Type]\n• Tenure: [Tenure Years] years\n• Products held: [Products Held]\n• Current balance: [Current Balance Range]\n\nMatch detail\n• Criteria met: [Eligibility Met Count] of [Eligibility Total]\n• Rate: [Current Rate]%\n\nThe value: ${vp}\n\nIf the timing's right, [Time Slot 1] for a 10-minute call.\n\n[Banker Name]\n[Title] · [Bank Name] · [Branch Name]`,
    },
  ];
  return cycle(variants, variant);
}

/**
 * Detects a Tune style from the combined chips + freeform text (Ryan's keyword
 * detection). Returns null when no style keyword is present (caller cycles to a
 * fresh variant instead).
 */
export function detectTuneStyle(input: string): TuneStyle {
  const p = input.toLowerCase();
  if (p.includes('shorter') || p.includes('concise') || p.includes('shorten')) return 'concise';
  if (p.includes('warm') || p.includes('friendly')) return 'warm';
  if (p.includes('formal')) return 'formal';
  return null;
}
